import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Loader2, Calendar } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CommunityPage = () => {
  const [communityItems, setCommunityItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCommunity = async () => {
      try {
        const response = await fetch(`${API}/community`);
        const data = await response.json();
        setCommunityItems(data);
      } catch (error) {
        console.error('Failed to fetch community items:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCommunity();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="community-page">
      <Navbar />
      
      {/* Header */}
      <section className="bg-gradient-to-r from-sip-purple to-sip-purple/80 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Heart className="w-16 h-16 mx-auto mb-4 text-white" />
            <h1 className="font-script text-4xl md:text-6xl text-white mb-4">
              Community Involvement
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Giving back to the communities that support us!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Community Items */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Highlighted Story */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-featured mb-12 max-w-4xl mx-auto"
          >
            <p className="text-lg text-gray-700 leading-relaxed">
              Community involvement is important to us as we value giving back and helping others whenever we can. Being part of our community means more than just running a business - it means building connections, supporting one another, and creating spaces where people can come together. We truly enjoy meeting new people and hearing their stories, and we believe that diversity is what makes our communities vibrant and strong. Different backgrounds, experiences, and perspectives are what make the world go round. <span className="font-semibold text-sip-purple">We would love the opportunity to help support your next fundraising event and be a part of making a positive impact together!</span>
            </p>
          </motion.div>

          {loading ? (
            <div className="text-center py-20" data-testid="loading-spinner">
              <Loader2 className="w-12 h-12 text-sip-purple animate-spin mx-auto" />
            </div>
          ) : communityItems.length === 0 ? (
            <div className="text-center py-20" data-testid="no-community-items">
              <div className="card-graffiti inline-block">
                <Heart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-xl text-gray-600">
                  No community events yet.
                </p>
                <p className="text-gray-500 mt-2">
                  Check back soon!
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              {communityItems.map((item, index) => (
                <motion.div
                  key={item.community_id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`card-graffiti ${index % 2 === 0 ? '' : 'md:flex-row-reverse'} md:flex items-center gap-8`}
                  data-testid={`community-item-${index}`}
                >
                  {item.image_url && (
                    <div className="md:w-1/2 mb-6 md:mb-0">
                      <div className="overflow-hidden rounded-2xl">
                        <img
                          src={item.image_url}
                          alt={item.title}
                          className="w-full h-64 object-cover"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className={item.image_url ? 'md:w-1/2' : 'w-full'}>
                    {item.date && (
                      <p className="flex items-center gap-2 text-sm text-sip-purple font-semibold mb-2">
                        <Calendar size={16} />
                        {item.date}
                      </p>
                    )}
                    <h3 className="font-semibold text-2xl text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600 text-lg leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-sip-yellow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-script text-3xl md:text-4xl text-gray-900 mb-4">
            Want Us at Your Community Event?
          </h2>
          <p className="text-xl text-gray-800 mb-6">
            We love partnering with local organizations, schools, and charities!
          </p>
          <a 
            href="/events" 
            className="btn-primary inline-block"
            data-testid="community-cta"
          >
            Get in Touch
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CommunityPage;
